import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Spinner } from '@telus-uds/components-web';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';

const FilterContainer = styled.div`
  background: #f7f7f7;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const FilterTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #4b286d;
  margin-bottom: 1rem;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #454545;
`;

const DatePickerWrapper = styled.div`
  .react-datepicker-wrapper {
    width: 100%;
  }

  input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.875rem;

    &:focus {
      outline: none;
      border-color: #4b286d;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const FilterPanel = ({
  filters, onFilterChange, onApply, onReset, onExport, isExporting, children
}) => {
  return (
    <FilterContainer>
      <FilterTitle>Filters</FilterTitle>
      {children || (
        <FilterGrid>
          {filters.map(filter => (
            <FilterItem key={filter.name}>
              <Label>{filter.label}</Label>
              {filter.type === 'date' && (
                <DatePickerWrapper>
                  <DatePicker
                    selected={filter.value ? new Date(filter.value) : null}
                    onChange={date => {
                      // Format date in local timezone to avoid UTC conversion
                      let formattedDate = null;
                      if (date) {
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        formattedDate = `${year}-${month}-${day}`;
                      }
                      onFilterChange(filter.name, formattedDate);
                    }}
                    dateFormat="yyyy-MM-dd"
                    placeholderText={filter.placeholder || 'Select date'}
                  />
                </DatePickerWrapper>
              )}
              {filter.type === 'select' && (
                <Select
                  value={filter.options.find(opt => opt.value === filter.value)}
                  onChange={option => onFilterChange(filter.name, option ? option.value : '')}
                  options={filter.options}
                  placeholder={filter.placeholder || 'Select...'}
                  isClearable
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              )}
              {filter.type === 'multiselect' && (
                <Select
                  value={filter.value}
                  onChange={selected => onFilterChange(filter.name, selected)}
                  options={filter.options}
                  placeholder={filter.placeholder || 'Select...'}
                  isMulti
                  isClearable
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              )}
            </FilterItem>
          ))}
        </FilterGrid>
      )}
      <ButtonGroup>
        <Button variant={{ priority: 'low' }} onClick={onReset}>
          Reset Filters
        </Button>
        <Button variant={{ priority: 'high' }} onClick={onApply}>
          Apply Filters
        </Button>
        {onExport && (
          <Spinner label="Exporting..." variant={{ size: 'small' }} show={!!isExporting} inline>
            <Button variant={{ priority: 'low' }} onClick={onExport} disabled={isExporting}>
              📊 Export to Excel
            </Button>
          </Spinner>
        )}
      </ButtonGroup>
    </FilterContainer>
  );
};

FilterPanel.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['date', 'select', 'multiselect']).isRequired,
      value: PropTypes.any,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any,
          label: PropTypes.string
        })
      ),
      placeholder: PropTypes.string
    })
  ),
  onFilterChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onExport: PropTypes.func,
  isExporting: PropTypes.bool,
  children: PropTypes.node
};

FilterPanel.defaultProps = {
  filters: [],
  onExport: null,
  isExporting: false,
  children: null
};

export default FilterPanel;
